import {useRef, useMemo} from 'react';
import {Map} from 'immutable';
import {setIn} from '@packages/systems/core/utils/ImmUtils';

import {
  EvalEnv,
  type AttributesMap,
  type ClassesList,
  type PluginsContext,
  type NormalizedAtomConfig,
} from '@packages/systems/wfdl';

import {type DynamoBindingProps} from '@packages/systems/dynamo/binding-props';
import {type BindingPathMapPojo} from '@packages/systems/dynamo/binding-types';
import {
  getDynBindingsFromElement,
  getSearchBindingsFromElement,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {usePropsMapper} from './usePropsMapper';
import {addNonDynamoCollectionIfNeeded} from './addNonDynamoCollectionIfNeeded';
import {weakMemo} from '@packages/utilities/memo';

import type {StaticBindings, DynamoPropsInput} from './types';

export type {DynamoPropsInput};

type DynamoProps = {
  evalElementProps: DynamoPropsInput;
  env: EvalEnv.EvalEnvType;
  pluginsContext: PluginsContext;
  dynamoAttributes?: AttributesMap;
  dynamoClasses?: ClassesList;
  dynamoBindingProps?: DynamoBindingProps;
  /** Legacy dyn/search binding paths, for binding-aware rendering during the loading window. */
  legacyDynamoBindingPaths: BindingPathMapPojo | undefined;
};

const defaultStaticBindings: StaticBindings = Map();
const defaultPathToReplaceableString = ['value'];
const EMPTY_BINDING_PATH_MAP = {} as BindingPathMapPojo;

const hasBindingPathValue = (
  bindingPath: BindingPathMapPojo[string]
): boolean => {
  if (Array.isArray(bindingPath)) {
    return bindingPath.length > 0;
  }

  // Fast path: assumes bindingPath is a plain object map.
  for (const key in bindingPath) {
    const path = bindingPath[key];
    if (path && path.length > 0) return true;
  }

  return false;
};

const getLegacyDynamoBindingPaths = (
  originalElement: DynamoPropsInput['originalElement']
): BindingPathMapPojo | undefined => {
  const searchBindings =
    getSearchBindingsFromElement(originalElement) ?? EMPTY_BINDING_PATH_MAP;
  const dynBindings =
    getDynBindingsFromElement(originalElement) ?? EMPTY_BINDING_PATH_MAP;

  let legacyDynamoBindingPaths: BindingPathMapPojo | undefined;

  // Dynamo wins, so process it first.
  for (const property in dynBindings) {
    const bindingPath = dynBindings[property];
    if (bindingPath && hasBindingPathValue(bindingPath)) {
      legacyDynamoBindingPaths ??= {};
      legacyDynamoBindingPaths[property] = bindingPath;
    }
  }

  // Search only applies when Dynamo did not declare the same property.
  for (const property in searchBindings) {
    if (Object.hasOwn(dynBindings, property)) continue;

    const bindingPath = searchBindings[property];
    if (bindingPath && hasBindingPathValue(bindingPath)) {
      legacyDynamoBindingPaths ??= {};
      legacyDynamoBindingPaths[property] = bindingPath;
    }
  }

  return legacyDynamoBindingPaths;
};

export {
  transformDynamoPropsForDesigner,
  transformDynamoPropsForExport,
} from './transformDynamoProps';

/**
 * React Hook that fetches GraphQL data for CMS, Ecommerce, User, and Site Search
 * bindings when needed, and returns an updated set of React props where the
 * dynamic data is baked in.
 *
 * In the future, this React Hook will set the fetched Dynamo data into `env`.
 * @param props
 * @param atomConfig
 * @returns {DynamoProps} Updated set of React Props that have Dynamo data baked in
 */
export function useDynamoProps(
  props: DynamoPropsInput,
  atomConfig: NormalizedAtomConfig
): DynamoProps {
  const {
    staticBindings = defaultStaticBindings,
    pathToReplaceableString = defaultPathToReplaceableString,
  } = atomConfig || {};
  // Captured before `props` is reassigned below, so the memo dep is not a mutated arg.
  const {originalElement} = props;
  // Some props mappers expect the dynamoContext.collections to include
  // some faux collections, e.g. Order Items collections. Once we make all the
  // props mappers working with Dynamo Gateway and Generic bindings, we no
  // longer need to add the faux collections here.
  // Future note: this will become redundant once we use binding contexts from
  // Dynamo Gateway.
  props = addNonDynamoCollectionIfNeeded(props);

  const {propsMapper, envMapper, loading} = usePropsMapper(
    props,
    staticBindings,
    pathToReplaceableString
  );

  const env = props.env;
  const updatedEnv = useMemo(() => envMapper(env), [envMapper, env]);

  const updatedProps = propsMapper(props, updatedEnv);

  // Stable per element, so compute once across the loading window.
  const legacyDynamoBindingPaths = useMemo(
    () => getLegacyDynamoBindingPaths(originalElement),
    [originalElement]
  );

  return useSyncedProps(
    {
      evalElementProps: props,
      env: updatedEnv,
      pluginsContext: updatedProps?.pluginsContext || props.pluginsContext,
      dynamoAttributes: updatedProps?.attributes || undefined,
      dynamoClasses: updatedProps?.classes || undefined,
      dynamoBindingProps: updatedProps?.dynamoBindingProps || undefined,
      legacyDynamoBindingPaths,
    },
    loading
  );
}

/**
 * This function is for getting the most recent props before loading Dynamo
 * data had started -- or the current props if the loading has already finished.
 * It keeps track of props that this function is called with when the loading is
 * not in progress.
 * @param props
 * @param loading
 * @returns {DynamoProps}
 */
function useSyncedProps(props: DynamoProps, loading: boolean): DynamoProps {
  const syncedProps = useRef<DynamoProps | null>(null);

  if (loading) {
    // Not having previously synced props means we are mounting the EvalElement.
    // We set the Dynamo context `loading` that expansions such as
    // DynamoDesignerExpansion relies on.
    return (
      syncedProps.current || {
        ...props,
        pluginsContext: setLoading(props.pluginsContext),
      }
    );
  }

  syncedProps.current = props;
  return props;
}

const setLoading = weakMemo(setIn(['Dynamo', 'loading'], true));
